import type { LocalDate } from "@js-joda/core";
import { ColorV2 } from "config/colors";
import { ApplicationResolution, ApplicationStatus } from "model/Application";
import { GigType } from "model/Gig";
import { toStockholmLocalDateFromUtcIso } from "web-apps/company/components/schedule/utils/schedule.dates";
import type { ApplicationFilter } from "./applicationList.filter.logic";

interface StaffingApplication {
  readonly gig: { readonly startTime?: string; readonly type: GigType };
}

interface GroupableStaffingApplication extends StaffingApplication {
  readonly gig: { readonly id: number; readonly type: GigType };
}

export interface StaffingShiftGroup<T extends GroupableStaffingApplication> {
  readonly applications: T[];
  readonly gigId: number;
}

interface ResolveStaffingPageParams<T extends StaffingApplication> {
  readonly applications: readonly T[];
  readonly currentPage: number;
  readonly pageSize: number;
}

interface FilterApplicationsByDateParams<T extends StaffingApplication> {
  readonly applications: readonly T[];
  readonly selectedDate: LocalDate | null;
}

interface ApplicationDateParams {
  readonly application: StaffingApplication;
  readonly selectedDate: LocalDate | null;
}

export interface StaffingPage<T> {
  readonly complete: boolean;
  readonly nextPage: number;
  readonly visibleApplications: T[];
}

interface ContinueStaffingPageFetchParams {
  readonly complete: boolean;
  readonly currentPage: number;
  readonly nextPage: number;
  readonly visibleApplicationCount: number;
}

interface StatusApplication {
  readonly resolution: ApplicationResolution;
  readonly status: ApplicationStatus;
}

interface ApplicationFilterMatchParams {
  readonly application: StatusApplication;
  readonly filter: ApplicationFilter;
}

export interface ApplicationStatusChip {
  readonly backgroundColor: ColorV2;
  readonly labelKey: string;
  readonly textColor: ColorV2;
}

export type ApplicationShiftType =
  | { type: "public"; iconName: "v2/globe" }
  | { type: "team"; iconName: "v2/team" }
  | { type: "unsupported" };

export function toApplicationShiftType(gigType: GigType): ApplicationShiftType {
  switch (gigType) {
    case GigType.PUBLIC:
      return { type: "public", iconName: "v2/globe" };
    case GigType.WORKPOOL:
      return { type: "team", iconName: "v2/team" };
    case GigType.AUTOGENERATED:
    case GigType.HIDDEN:
    case GigType.SOLO:
    case GigType.STAFFING:
    case GigType.UNIT:
    case GigType.WORKGROUP:
      return { type: "unsupported" };
    default:
      return { type: "unsupported" };
  }
}

export function isVisibleStaffingApplication(
  application: StaffingApplication,
): boolean {
  return toApplicationShiftType(application.gig.type).type !== "unsupported";
}

export function groupStaffingApplicationsByShift<
  T extends GroupableStaffingApplication,
>(applications: readonly T[]): StaffingShiftGroup<T>[] {
  const groups = new Map<number, T[]>();

  applications.forEach((application) => {
    const existingApplications = groups.get(application.gig.id);
    if (existingApplications) {
      existingApplications.push(application);
      return;
    }
    groups.set(application.gig.id, [application]);
  });

  return Array.from(groups, ([gigId, groupedApplications]) => ({
    applications: groupedApplications,
    gigId,
  }));
}

export function resolveStaffingPage<T extends StaffingApplication>(
  params: ResolveStaffingPageParams<T>,
): StaffingPage<T> {
  const complete = params.applications.length < params.pageSize;
  return {
    complete,
    nextPage: complete ? params.currentPage : params.currentPage + 1,
    visibleApplications: params.applications.filter(
      isVisibleStaffingApplication,
    ),
  };
}

export function shouldContinueStaffingPageFetch(
  params: ContinueStaffingPageFetchParams,
): boolean {
  return (
    params.visibleApplicationCount === 0 &&
    !params.complete &&
    params.nextPage > params.currentPage
  );
}

export function filterApplicationsByDate<T extends StaffingApplication>({
  applications,
  selectedDate,
}: FilterApplicationsByDateParams<T>): T[] {
  if (!selectedDate) return [...applications];
  return applications.filter((application) =>
    isApplicationOnDate({ application, selectedDate }),
  );
}

function isApplicationOnDate({
  application,
  selectedDate,
}: ApplicationDateParams): boolean {
  if (!selectedDate) return true;
  const applicationDate = toStockholmLocalDateFromUtcIso(
    application.gig.startTime,
  );
  return applicationDate?.equals(selectedDate) ?? false;
}

export function toApplicationStatusChip(
  application: StatusApplication,
): ApplicationStatusChip | null {
  if (application.status === ApplicationStatus.OFFERED) {
    return {
      backgroundColor: ColorV2.OfferedYellow,
      labelKey: "GigsCompany.Offered",
      textColor: ColorV2.Blue1,
    };
  }

  switch (application.resolution) {
    case ApplicationResolution.ACCEPTED:
      return {
        backgroundColor: ColorV2.PositiveGreenLight,
        labelKey: "ApplicationsCompany.Hired",
        textColor: ColorV2.SimflexityGreen,
      };
    case ApplicationResolution.REJECTED:
    case ApplicationResolution.DECLINED:
      return {
        backgroundColor: ColorV2.NegativeRedLight,
        labelKey:
          application.resolution === ApplicationResolution.REJECTED
            ? "GigsCompany.Rejected"
            : "GigsCompany.Declined",
        textColor: ColorV2.NegativeRed,
      };
    case ApplicationResolution.WITHDRAWN:
      return {
        backgroundColor: ColorV2.WithdrawnPink,
        labelKey: "Schedule.Withdrawn",
        textColor: ColorV2.Blue1,
      };
    case ApplicationResolution.UNDEFINED:
      return null;
    default:
      return null;
  }
}

export function matchesApplicationFilter({
  application,
  filter,
}: ApplicationFilterMatchParams): boolean {
  switch (filter) {
    case "All":
      return true;
    case "Accepted":
      return (
        application.status !== ApplicationStatus.OFFERED &&
        application.resolution === ApplicationResolution.ACCEPTED
      );
    case "Declined":
      return (
        application.status !== ApplicationStatus.OFFERED &&
        (application.resolution === ApplicationResolution.DECLINED ||
          application.resolution === ApplicationResolution.REJECTED)
      );
    case "Offered":
      return application.status === ApplicationStatus.OFFERED;
    case "Withdrawn":
      return (
        application.status !== ApplicationStatus.OFFERED &&
        application.resolution === ApplicationResolution.WITHDRAWN
      );
    default:
      return false;
  }
}
